import React, { Dispatch } from "react";
import { AioExternalSingle } from "../additional/interface";
import { GridItemTrafficLight } from "../table/functions/GridItemTrafficLight";
import { EditorDefns } from "../types/interface";
import { AibBlock } from "./AibBlock";
import { BlockContextProvider } from "./BlockContentProvider";
import { AibBlockLine, AibLineType } from "./interface";

export interface AsupInternalBlockProps<T> {
  id: string;
  canAddFirstRow?: boolean;
  canChangeType?: boolean;
  changedBlocks?: GridItemTrafficLight[];
  defaultType?: AibLineType;
  disabled?: boolean;
  editorProps: EditorDefns<T>;
  externalSingles?: AioExternalSingle<T>[];
  lineStyle?: React.CSSProperties;
  lines: AibBlockLine<T>[];
  maxLines?: number;
  minLines?: number;
  noComments?: boolean;
  noTrafficLights?: boolean;
  onChange: Dispatch<AibBlockLine<T>[]>;
}
/**
 * Renders the AsupInternalBlock component, which is the collection of lines
 *
 * @param props - Destructured parameter object
 * @param props.id - Unique id used as the base for DOM attributes
 * @param props.canAddFirstRow - When true, a control to add the first row is enabled
 * @param props.canChangeType - When true, lines may change their type via UI controls
 * @param props.changedBlocks - Array of changed blocks used to highlight modified grid items
 * @param props.defaultType - Default line display type applied to newly created lines
 * @param props.disabled - When true, editing controls are disabled
 * @param props.editorProps - Editor configuration passed to child cell editors
 * @param props.externalSingles - External single-value lists available to cells
 * @param props.lineStyle - Inline style object applied to each line container
 * @param props.lines - Array of block lines to render
 * @param props.maxLines - Maximum number of lines allowed in the block
 * @param props.minLines - Minimum number of lines allowed in the block
 * @param props.noComments - When true, comment UI is suppressed
 * @param props.noTrafficLights - When true, traffic light indicators are hidden
 * @param props.onChange - Callback invoked when the set of lines changes
 */
export const AsupInternalBlock = <T,>({
  id,
  canAddFirstRow = false,
  canChangeType = false,
  changedBlocks = [],
  defaultType = AibLineType.centerOnly,
  disabled = false,
  editorProps,
  externalSingles = [],
  lineStyle = {},
  lines,
  maxLines = 10,
  minLines = 1,
  noComments = false,
  noTrafficLights = false,
  onChange,
}: AsupInternalBlockProps<T>): React.ReactElement => {
  return (
    <BlockContextProvider
      initialState={{
        id,
        canAddFirstRow,
        canChangeType,
        changed: false,
        changedBlocks,
        defaultType,
        disabled,
        editorProps,
        externalSingles,
        lineStyle,
        lines,
        maxLines,
        minLines,
        noComments,
        noTrafficLights,
      }}
      onChange={onChange}
    >
      <AibBlock />
    </BlockContextProvider>
  );
};

AsupInternalBlock.displayName = "AsupInternalBlock";
